import React, { useState, useEffect, useRef, useCallback, memo, forwardRef, useImperativeHandle } from "react";
import { Colors } from "styles/global-style";
import foldarrow from "imgs/foldarrow.png";

const WorkItem = ({ text, active, expanded, isLeaf, isFirst, animationDone, onClickCb, onDoubleClickCb, onChangeCb, item, collapsed, onToggleCollapse, users }, outerRef) => {
  const [hover, setHover] = useState(false);
  const [textValue, setTextValue] = useState(text);
  const [background, setBackground] = useState("#ffffff13");
  const [color, setColor] = useState("white");
  const [editing, setEditing] = useState(false);
  const [width, setWidth] = useState();

  const containerRef = useRef();
  const innerRef = useRef();
  useImperativeHandle(outerRef, () => innerRef.current, []);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const handleChange = useCallback(
    (e) => {
      setTextValue(e.target.value);
      // Don't call onChangeCb on every keystroke - only update local state
    },
    [setTextValue]
  );

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      onClickCb(e);
    },
    [onClickCb]
  );

  const clickListener = useCallback(
    (e) => {
      if (!containerRef.current?.contains(e.target)) {
        window.removeEventListener("mousedown", clickListener);
        setEditing(false);
      }
    },
    [containerRef, setEditing]
  );

  const handleDoubleClick = useCallback(
    (e) => {
      e.stopPropagation();
      setEditing(true);
      onDoubleClickCb(e);
      window.addEventListener("mousedown", clickListener);
    },
    [onDoubleClickCb, setEditing, clickListener]
  );

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useEffect(() => {
    // innerRef.current.style.width = `${Math.max(textValue.getTextWidth(innerRef.current) + 10, 30)}px`;
    setWidth(Math.max(textValue.getTextWidth(innerRef.current) + 10, 30));
  }, [textValue, innerRef]);

  useEffect(() => {
    if (isLeaf) {
      setBackground("#ffffffee");
      setColor("black");
    } else {
      setBackground("linear-gradient(to right, #ffffff16, transparent)");
      setColor("#ddd");
    }
  }, [isLeaf, setBackground, setColor]);

  useEffect(() => {
    return () => window.removeEventListener("mousedown", clickListener);
  }, [clickListener]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  return (
    <>
      <div 
        className="workitem" 
        style={{ 
          display: "flex", 
          alignItems: "start", 
          gap: 8,
          opacity: animationDone || isFirst ? 1 : 0
        }}
      >
        {/* Foldarrow for stories - outside the item box */}
        {item?.type === 'story' && (
          <div style={{ height: "100%", display: "flex", alignItems: "center", alignSelf: "center" }}>
            {item.hasTasks ? (
              <img
                src={foldarrow}
                alt="collapse"
                style={{
                  width: 11,
                  height: 11,
                  cursor: "pointer",
                  transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
                  transition: "transform 0.2s ease",
                  filter: "invert(1)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse && onToggleCollapse(item.storyId);
                }}
              />
            ) : (
              <div style={{ width: 11, height: 11 }} /> // Placeholder space
            )}
          </div>
        )}
        
        {/* The actual workitem box */}
        <div
          ref={containerRef}
          className={item?.type === 'task' ? 'workitem-task-container' : ''}
          style={{
            display: "flex",
            alignItems: "start",
            gap: 8,
            position: "relative",
            justifyContent: "space-between",
            minWidth: collapsed ? 0 : 200,
            outline: active ? (isLeaf ? "3px solid" + Colors.ORANGE : "2px solid #ffffff44") : "1px solid transparent",
            borderRadius: 4,
            padding: "4px 8px",
            cursor: "pointer",
            background: hover ? (isLeaf ? "#fff" : "linear-gradient(to right, #ffffff33, transparent)") : background,
            userSelect: "none",
            height: editing ? 80 : (expanded && item?.type === 'task' ? 100 : 19),
            width: editing ? 450 : (collapsed ? 'fit-content' : (item?.type === 'task' ? (expanded ? 600 : 300) : 200)),
            transition: "height 100ms ease-out, width 100ms ease-out",
          }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
        >
          {/* {hasParent && <div style={{ position: "absolute", left: isLeaf ? -18 : -15, top: "50%", transform: "translateY(-79%)", color: "white", opacity: 0.3 }}>↳</div>} */}
          
          <input
            ref={innerRef}
            style={{
              background: "transparent",
              outline: "none",
              color: color,
              border: "none",
              fontWeight: isLeaf ? 500 : 700,
              pointerEvents: editing ? "all" : "none",
              width: width,
              flex: 1,
              minWidth: 0,
              marginTop: 1,
              maxWidth: collapsed ? 180 : undefined,
              overflow: collapsed ? "hidden" : (!expanded && item?.type === 'task' ? "hidden" : "visible"),
              textOverflow: collapsed ? "ellipsis" : (!expanded && item?.type === 'task' ? "ellipsis" : "clip"),
              whiteSpace: collapsed ? "nowrap" : (!expanded && item?.type === 'task' ? "nowrap" : "normal"),
            }}
            value={textValue}
            onChange={handleChange}
            onFocus={() => setEditing(true)}
            onBlur={() => {
              setEditing(false);
              // Call onChangeCb when editing is complete
              onChangeCb({ target: { value: textValue } });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setEditing(false);
                // Call onChangeCb when user presses Enter
                onChangeCb({ target: { value: textValue } });
                e.target.blur(); // Remove focus
              }
            }}
            placeholder="Item"
          />
          
          {/* Progress, estimate, and owner circle for tasks */}
          {item?.type === 'task' && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8, height: 19 }}>
              {/* Progress indicator */}
              {item.columnName && (
                <div style={{
                  fontSize: 9,
                  padding: '1px 4px',
                  borderRadius: 2,
                  background: item.columnName === 'DONE' ? '#4CAF50' : 
                             item.columnName === 'IN PROGRESS' ? '#FF9800' : 
                             item.columnName === 'REVIEW' ? '#2196F3' : '#9E9E9E',
                  color: 'white',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}>
                  {item.columnName === 'TO DO' ? 'TODO' : item.columnName}
                </div>
              )}
              
              {/* Estimate - only show when task is expanded */}
              {expanded && item.estimate && (
                <div style={{ fontWeight: "bold", color: isLeaf ? "#555" : "#ffffff88", fontSize: 11 }}>
                  {item.estimate > 8 ? Math.floor(item.estimate / 8) + "d" + (item.estimate % 8 !== 0 ? " " + (item.estimate % 8) + "h" : "") : item.estimate + "h"}
                </div>
              )}
              
              {/* Owner circle */}
              {(item.owner_id || item.person_id) && users && Array.isArray(users) && (() => {
                const ownerId = item.owner_id || item.person_id;
                const user = users.find(u => u.id === ownerId);
                return user ? (
                  <div style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: user.avatar ? 'transparent' : '#999',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 6,
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    position: 'relative',
                    flexShrink: 0,
                  }}>
                    {user.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={`${user.firstname} ${user.lastname || ''}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = '#999';
                        }}
                      />
                    ) : null}
                    {!user.avatar && (
                      <span>
                        {user.firstname ? user.firstname.substring(0, 1).toUpperCase() + user.firstname.substring(1, 2).toLowerCase() : ''}{user.lastname ? user.lastname.substring(0, 1).toUpperCase() : ''}
                      </span>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default memo(forwardRef(WorkItem));
